import React, { useEffect, useState } from 'react'
import './styles/index.css'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { Col, Divider, Space, message, theme } from 'antd'
import CustomRow from '../../../../reusable-components/CustomRow'
import { confirmAfterHours, isPublicHoliday } from './utils/search-engine-widget-utils'
import { handleError } from '../../../../lib/utils'
import AgiliteSkeleton from '../../../../reusable-components/AgiliteSkeleton'
import { deviceDetect } from 'react-device-detect'

const BookingSummary = ({ booking, patient, serviceInfo, isVirtualVisit, isScheduledBooking }) => {
  // Helper function to check if the service is flexi or priority
  const isFlexiOrPriorityConsult = () => {
    // Check both name and label properties for service name
    const serviceName = serviceInfo?.name?.toLowerCase() || serviceInfo?.label?.toLowerCase() || ''
    // Check service name first
    if (serviceName.includes('flexi') || serviceName.includes('priority')) {
      return true
    }
    // Fallback: check if it's a virtual visit without assigned doctor and time
    if (isVirtualVisit && !booking.medicalProf && !booking.startTime) {
      return true
    }
    return false
  }

  // Helper function to get service type
  const getServiceType = () => {
    // Check for priority consultation indicators
    // Since state flow between components is broken, we'll use fallback detection
    if (isVirtualVisit && !booking.medicalProf && !booking.startTime) {
      // Check session storage for priority flag set by SearchDetailsCapture
      const storedPriorityFlag = sessionStorage.getItem('isCurrentBookingPriority')

      // If session storage indicates priority, treat as priority
      if (storedPriorityFlag === 'true') {
        return 'priority'
      } else if (storedPriorityFlag === 'false') {
        return 'flexi'
      }
    }

    // First, check explicit priority flags in booking object (most reliable)
    if (
      booking.isPriorityConsult === true ||
      booking.serviceType === 'priority' ||
      booking.consultType === 'priority' ||
      booking.priorityConsult === true ||
      booking.type === 'priority'
    ) {
      return 'priority'
    }

    // Check both name and label properties for service name
    const serviceName = serviceInfo?.name?.toLowerCase() || serviceInfo?.label?.toLowerCase() || ''

    // Check service name
    if (serviceName.includes('flexi')) return 'flexi'
    if (serviceName.includes('priority')) return 'priority'

    // Fallback detection for virtual visits without assigned doctor/time
    if (isVirtualVisit && !booking.medicalProf && !booking.startTime) {
      // Check for priority indicators in service name or booking data
      if (
        serviceName.includes('urgent') ||
        serviceName.includes('immediate') ||
        serviceName.includes('priority') ||
        booking.priority
      ) {
        return 'priority'
      }
      return 'flexi'
    }

    return 'scheduled'
  }

  // Helper function to get proper service title
  const getServiceTitle = () => {
    if (isVirtualVisit) {
      const serviceType = getServiceType()
      switch (serviceType) {
        case 'flexi':
          return 'Flexi Virtual Consult'
        case 'priority':
          return 'Priority Virtual Consult'
        default:
          return 'Scheduled Virtual Consult'
      }
    } else if (booking.clinicRef === 'Home Visit') {
      return 'Book Home Visit'
    } else {
      return 'Book Clinic Visit'
    }
  }

  // Helper function to get medical professional display
  const getMedicalProfessional = () => {
    if (booking.medicalProf) {
      return `Dr. ${booking.medicalProf.firstName} ${booking.medicalProf.lastName}`
    } else if (isFlexiOrPriorityConsult()) {
      return 'Meta-Health Verified Doctor'
    } else {
      return 'To be assigned'
    }
  }

  // Helper function to get timeslot display
  const getTimeslotDisplay = () => {
    const serviceType = getServiceType()
    if (serviceType === 'flexi') return 'Flexi'
    if (serviceType === 'priority') return 'Priority'
    return booking.startTime || 'To be assigned'
  }

  const clinicState = useSelector((state) => state.clinics.data)
  const authState = useSelector((state) => state.auth)
  const [afterHourDetails, setAfterHoursDetails] = useState(null)
  const [loading, setLoading] = useState(false)

  const generatePatientInfo = () => {
    if (patient) {
      return `${patient.firstName} ${patient.lastName}`
    } else {
      return `${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`
    }
  }

  useEffect(() => {
    // Debug logging to understand the data
    console.log('BookingSummary Debug:', {
      serviceInfo,
      serviceName: serviceInfo?.name,
      serviceLabel: serviceInfo?.label,
      combinedServiceName: serviceInfo?.name?.toLowerCase() || serviceInfo?.label?.toLowerCase() || '',
      booking,
      isVirtualVisit,
      hasDoctor: !!booking.medicalProf,
      hasStartTime: !!booking.startTime,
      detectedType: getServiceType(),
      isFlexiOrPriority: isFlexiOrPriorityConsult(),
      // Check for priority flags in booking object
      'booking.isPriorityConsult': booking.isPriorityConsult,
      'booking.serviceType': booking.serviceType,
      'booking.consultType': booking.consultType
    })
    handleConfirmAfterHours()
    // eslint-disable-next-line
  }, [])

  const handleConfirmAfterHours = async () => {
    let tmpAfterHourDetails = null
    setLoading(true)
    try {
      // For flexi/priority consultations without specific time slots, skip after-hours check
      if (booking.startTime && !isFlexiOrPriorityConsult()) {
        tmpAfterHourDetails = await confirmAfterHours(dayjs(booking.bookingDate).day(), booking.startTime)
      }
      setAfterHoursDetails(tmpAfterHourDetails)
    } catch (e) {
      message.error(handleError(e))
    }
    setLoading(false)
  }
  const { token } = theme.useToken()

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(16, 178, 225, 0.1)',
        overflow: 'hidden'
      }}
    >
      {loading ? (
        <div style={{ padding: '40px' }}>
          <AgiliteSkeleton skActive spinnerTip='Loading booking details...' />
        </div>
      ) : (
        <>
          {/* Header Section */}
          <div
            style={{
              background: `linear-gradient(135deg, ${token.colorPrimary} 0%, rgba(16, 178, 225, 0.8) 100%)`,
              color: 'white',
              padding: '20px 24px',
              textAlign: 'center',
              position: 'relative'
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                pointerEvents: 'none'
              }}
            />
            <h2
              style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 700,
                letterSpacing: '0.5px',
                position: 'relative',
                zIndex: 1
              }}
            >
              {getServiceTitle()}
            </h2>
          </div>

          {/* Content Section */}
          <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
              {/* Date Display - Left Side */}
              {!deviceDetect().isMobile && (
                <div
                  style={{
                    background: `linear-gradient(135deg, ${token.colorPrimary} 0%, rgba(16, 178, 225, 0.8) 100%)`,
                    borderRadius: '12px',
                    padding: '16px',
                    minWidth: '80px',
                    textAlign: 'center',
                    color: 'white',
                    flexShrink: 0
                  }}
                >
                  <div style={{ fontSize: '24px', fontWeight: 700, lineHeight: 1 }}>
                    {dayjs(booking.bookingDate).isValid()
                      ? dayjs(booking.bookingDate).format('DD')
                      : dayjs().format('DD')}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 600, marginTop: '4px' }}>
                    {dayjs(booking.bookingDate).isValid()
                      ? dayjs(booking.bookingDate).format('MMM').toUpperCase()
                      : dayjs().format('MMM').toUpperCase()}
                  </div>
                </div>
              )}

              {/* Details - Right Side */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'grid', gap: '16px' }}>
                  {deviceDetect().isMobile && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#8c8c8c', fontSize: '14px' }}>Date:</span>
                      <span style={{ fontWeight: 600, color: '#262626' }}>
                        {dayjs(booking.bookingDate).format('DD MMM YYYY')}
                      </span>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#8c8c8c', fontSize: '14px' }}>Medical Professional:</span>
                    <span style={{ fontWeight: 600, color: '#262626' }}>{getMedicalProfessional()}</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#8c8c8c', fontSize: '14px' }}>Appointment For:</span>
                    <span style={{ fontWeight: 600, color: '#262626' }}>{generatePatientInfo()}</span>
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#8c8c8c', fontSize: '14px' }}>Timeslot:</span>
                    <span
                      style={{
                        fontWeight: 600,
                        color: getServiceType() === 'scheduled' ? '#262626' : token.colorPrimary,
                        fontSize: getServiceType() !== 'scheduled' ? '16px' : '14px'
                      }}
                    >
                      {getTimeslotDisplay()}
                    </span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#8c8c8c', fontSize: '14px' }}>Type:</span>
                    <span style={{ fontWeight: 600, color: '#262626' }}>
                      {isVirtualVisit ? getServiceTitle() : serviceInfo?.name || 'Service'}
                    </span>
                  </div>

                  {booking.clinicRef !== 'Home Visit' && !isVirtualVisit && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#8c8c8c', fontSize: '14px' }}>Location:</span>
                      <span style={{ fontWeight: 600, color: '#262626' }}>
                        {clinicState.find((i) => i._id === booking.clinicRef)?.name || 'Clinic'}
                      </span>
                    </div>
                  )}

                  <Divider style={{ margin: '8px 0' }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#8c8c8c', fontSize: '14px' }}>Appointment Fee:</span>
                    <span style={{ fontWeight: 600, color: '#262626' }}>R{serviceInfo?.rate || 0}</span>
                  </div>

                  {(afterHourDetails || isPublicHoliday(booking.bookingDate)) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#8c8c8c', fontSize: '14px' }}>After Hours Fee:</span>
                      <span style={{ fontWeight: 600, color: '#262626' }}>R200</span>
                    </div>
                  )}

                  {!isScheduledBooking && (
                    <>
                      {afterHourDetails || isPublicHoliday(booking.bookingDate) ? (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px',
                            background: '#f0f9ff',
                            borderRadius: '8px',
                            border: '1px solid #0ea5e9'
                          }}
                        >
                          <span style={{ color: '#0369a1', fontSize: '14px', fontWeight: 600 }}>Total Fee:</span>
                          <span style={{ fontWeight: 700, color: '#0369a1', fontSize: '16px' }}>
                            R{(serviceInfo?.rate || 0) + 200}
                          </span>
                        </div>
                      ) : (
                        <div
                          style={{
                            padding: '12px',
                            background: '#fff7ed',
                            borderRadius: '8px',
                            border: '1px solid #fb923c',
                            fontSize: '13px',
                            color: '#ea580c'
                          }}
                        >
                          Please note: A public holiday fee of R200 may be levied.
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default BookingSummary
